import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Loader2, LogIn } from "lucide-react";
import { useT } from "@/i18n";
import "@/styles/crm.css";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";

function formatError(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function PortalLogin() {
  const { login } = usePortalAuth();
  const navigate = useNavigate();
  const t = useT();
  const p = t.portal;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/portal");
    } catch (err) { setError(formatError(err.response?.data?.detail) || t.consultar?.error || "Error"); }
    setLoading(false);
  };

  return (
    <div className="crm-page min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="NewSky" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold" style={{ color: "var(--crm-text)" }} data-testid="portal-login-title">{p.loginTitle}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--crm-text-muted)" }}>{p.loginSub}</p>
        </div>

        <form onSubmit={handleSubmit} className="crm-card p-6 space-y-4" data-testid="portal-login-form">
          {error && <div className="p-3 rounded-xl text-sm" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>}
          <div>
            <label className="crm-label">{p.email}</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-login-email" className="crm-input" placeholder={p.emailPh} />
          </div>
          <div>
            <label className="crm-label">{p.password}</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-login-password" className="crm-input" placeholder={p.passwordPh} />
          </div>
          <button type="submit" disabled={loading} data-testid="portal-login-submit" className="crm-btn-primary w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} {p.enter}
          </button>
          <div className="text-right">
            <button type="button" onClick={() => setShowReset(true)} className="text-xs transition-colors" style={{ color: "var(--crm-text-dim)" }} data-testid="portal-forgot-password">
              {p.forgot}
            </button>
          </div>
        </form>

        {showReset && (
          <div className="crm-card p-5 mt-4" data-testid="portal-reset-info">
            <p className="text-sm font-bold mb-2" style={{ color: "var(--crm-text)" }}>{p.forgotTitle}</p>
            <p className="text-xs leading-relaxed mb-3" style={{ color: "var(--crm-text-muted)" }}>{p.forgotDesc}</p>
            <div className="space-y-1.5">
              <a href="https://wa.me/819045660098" target="_blank" rel="noopener noreferrer" className="text-xs font-medium flex items-center gap-1" style={{ color: "var(--crm-accent)" }}>WhatsApp: +81-90-4566-0098</a>
              <a href="mailto:internet@newsky.jp" className="text-xs font-medium" style={{ color: "var(--crm-accent)" }}>Email: internet@newsky.jp</a>
            </div>
            <button onClick={() => setShowReset(false)} className="mt-3 text-xs transition-colors" style={{ color: "var(--crm-text-dim)" }}>{p.forgotClose}</button>
          </div>
        )}

        {/* Demo button */}
        <div className="mt-4">
          <button
            onClick={async () => { setError(""); setLoading(true); try { await login("test@example.com", "test123"); navigate("/portal"); } catch (err) { setError(formatError(err.response?.data?.detail) || "Error"); } setLoading(false); }}
            disabled={loading}
            data-testid="portal-demo-button"
            className="crm-btn-secondary w-full"
          >
            {p.demo}
          </button>
          <p className="text-center text-xs mt-2" style={{ color: "var(--crm-text-dim)" }}>test@example.com / test123</p>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-xs transition-colors" style={{ color: "var(--crm-text-dim)" }}>{p.backToSite}</Link>
        </div>
      </div>
    </div>
  );
}
